import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import { Button, Modal } from '@/components/ui'
import { PlanSummary } from '@/components/billing/PlanSummary'
import { CheckoutEmbed } from '@/components/billing/CheckoutEmbed'
import { InvoiceList } from '@/components/billing/InvoiceList'
import { AddOnManager } from '@/components/billing/AddOnManager'
import { UsageChart } from '@/components/billing/UsageChart'
import { InvoiceRecipients } from './billing/InvoiceRecipients'
import { formatDate } from '@/utils/format-date'

export function BillingPage() {
  const { orgId } = useCurrentOrgId()
  const queryClient = useQueryClient()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showReactivateCheckout, setShowReactivateCheckout] = useState(false)

  const { data: billingStatus, isLoading, error } = useQuery({
    queryKey: ['billing-status', orgId],
    queryFn: () => api.getBillingStatus(orgId ?? ''),
    enabled: !!orgId,
  })

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelSubscription(orgId ?? ''),
    onSuccess: () => {
      setShowCancelModal(false)
      void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
    },
  })

  const handlePortalRedirect = useCallback(async (flow?: string) => {
    if (!orgId) return
    const session = await api.createPortalSession(orgId, {
      returnUrl: window.location.href,
      flow,
    })
    window.location.href = session.url
  }, [orgId])

  const handleCheckoutComplete = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
    setShowReactivateCheckout(false)
  }, [queryClient])

  if (isLoading) return <BillingSkeleton />

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load billing information.</p>
      </div>
    )
  }

  if (!billingStatus) return null

  const { organization } = billingStatus
  const isTrialOrNoSub = organization.status === 'trial' || organization.status === 'inactive'
  const isActive = organization.status === 'active' || organization.status === 'past_due'
  const isCanceled = organization.status === 'canceled'
  const deletionDate = organization.deletionScheduledAt
    ? new Date(organization.deletionScheduledAt)
    : null
  const canReactivate = isCanceled && deletionDate && deletionDate > new Date()

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Billing</h1>

      {/* Plan Summary */}
      <PlanSummary billingStatus={billingStatus} />

      {/* Checkout for trial/inactive */}
      {isTrialOrNoSub && (
        <CheckoutEmbed onComplete={handleCheckoutComplete} />
      )}

      {/* Reactivate checkout for canceled */}
      {canReactivate && !showReactivateCheckout && (
        <div className="card px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Subscription Canceled
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Your account is scheduled for deletion on{' '}
                {formatDate(deletionDate)}.
                Reactivate to keep your canaries.
              </p>
            </div>
            <Button onClick={() => { setShowReactivateCheckout(true) }}>
              Reactivate
            </Button>
          </div>
        </div>
      )}

      {canReactivate && showReactivateCheckout && (
        <CheckoutEmbed onComplete={handleCheckoutComplete} />
      )}

      {/* Add-on management */}
      {isActive && (
        <AddOnManager billingStatus={billingStatus} />
      )}

      {/* Daily Usage Chart */}
      {(isActive || isTrialOrNoSub) && (
        <UsageChart />
      )}

      {/* Payment Method */}
      {isActive && (
        <div className="card px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Payment Method
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Update your payment method via the Stripe Customer Portal.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => { void handlePortalRedirect('payment_method_update') }}
            >
              Manage Payment Method
            </Button>
          </div>
        </div>
      )}

      {/* Invoices */}
      <InvoiceList />

      {/* Invoice Recipients */}
      <InvoiceRecipients />

      {/* Cancel Subscription */}
      {isActive && (
        <div className="card px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Cancel Subscription
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Your subscription will remain active until the end of the current billing period.
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => { setShowCancelModal(true) }}
            >
              Cancel Subscription
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false) }}
        title="Cancel Subscription"
        footer={
          <>
            <Button
              variant="danger"
              isLoading={cancelMutation.isPending}
              onClick={() => { cancelMutation.mutate() }}
            >
              Confirm Cancellation
            </Button>
            <Button
              variant="ghost"
              className="mr-3"
              onClick={() => { setShowCancelModal(false) }}
            >
              Keep Subscription
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel your subscription? Your canaries will remain active
          until the end of the current billing period, after which your account will be
          deactivated and data deletion will be scheduled.
        </p>
        {cancelMutation.isError && (
          <p className="mt-3 text-sm text-danger-600">
            Failed to cancel subscription. Please try again.
          </p>
        )}
      </Modal>
    </div>
  )
}

function BillingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 bg-gray-200 rounded w-32 animate-pulse" />
      <div className="card p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i}>
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-28" />
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-16" />
          <div className="h-2 bg-gray-200 rounded" />
          <div className="h-2 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  )
}
